import React, { useMemo } from 'react';
import { Button } from '../Button';
import { MenuTool } from '../MenuTool';
import { Tool } from '../Tool';
import type { CustomCell, CustomRow, TableAction, TableProps } from '../Table';
import type { WindowDimension } from '../../../lib';
import {
  intl,
  notifyConfirm,
  useAccessibleElementProps,
  useSingleAndDoubleClick,
  useWindowResize
} from '../../../lib';
import { HBox } from '../../layout';

// eslint-disable-next-line @typescript-eslint/no-restricted-types
type DataType = object;

/* Screen breakpoints (Bootstrap-like) */
/* TODO: move this to constants to make it reusable */
const breakpoints = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200
};

/**
 * Props for a {@link TableRow}.
 */
export type TableRowProps<RowDataType extends DataType> = Pick<
  TableProps<RowDataType>,
  | 'squashActions'
  | 'actions'
  | 'onRowClick'
  | 'onRowDoubleClick'
  | 'breakpoint'
  | 'hideableColumns'
> & {
  /** Data of the row that should be rendered. */
  row: CustomRow<RowDataType>;
  /** Index of the row. */
  rowIndex: number;
  /** Method for preparing the row, contained in the tableInstance. */
  prepareRow: TableProps<RowDataType>['tableInstance']['prepareRow'];
};

/**
 * Component for a Row of a Table.
 *
 * @param parameters {@link TableRowProps}
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function TableRow<RowDataType extends DataType>({
  row,
  prepareRow,
  actions,
  rowIndex,
  squashActions = false,
  onRowClick,
  onRowDoubleClick,
  breakpoint,
  hideableColumns
}: TableRowProps<RowDataType>): React.JSX.Element {
  const windowDimensions: WindowDimension = useWindowResize();
  const isResponsiveView =
    breakpoint && windowDimensions.width <= breakpoints[breakpoint];

  // row single and double click handler
  const rowClick = useMemo(() => {
    return onRowClick ? () => onRowClick(row) : undefined;
  }, [onRowClick, row]);
  const rowDblClick = useMemo(() => {
    return onRowDoubleClick ? () => onRowDoubleClick(row) : undefined;
  }, [onRowDoubleClick, row]);
  const [
    rowSingleClickHandler,
    rowDoubleClickHandler
  ] = useSingleAndDoubleClick(rowClick, rowDblClick);

  // handle the actions properly
  const rowActions = actions?.filter((action: TableAction<RowDataType>) => {
    // if no renderCondition is set, the action is always shown
    if (action.renderCondition === undefined) {
      return true;
    }

    // filter out action if renderCondition doesn't evaluate to `true`
    return typeof action.renderCondition === 'function'
      ? action.renderCondition(row)
      : action.renderCondition;
  });
  const isActionDisabled = (
    action: TableAction<RowDataType>,
    rowData: CustomRow<RowDataType>
  ): boolean =>
    typeof action.disabled === 'function'
      ? action.disabled(rowData)
      : !!action.disabled;

  const handleActionClick = (
    action: TableAction<RowDataType>,
    rowData: typeof row
  ) => {
    if (action.confirmation) {
      notifyConfirm({
        message:
          action?.confirmation?.message || intl('view.table.confirm.message'),
        onConfirm: () => action.onClick(rowData),
        title: action?.confirmation?.title || intl('view.table.confirm.title')
      });
    } else {
      action.onClick(rowData);
    }
  };

  // function to build the row className
  const getClassName = () => {
    const className = [];
    // it's possible to pass a className in the rowProps, so that also needs to be considered
    const rowPropsClassName = row?.getRowProps?.()?.className;

    if (row?.isSelected) className.push('selected');
    if (rowPropsClassName) className.push(rowPropsClassName);

    return className.join(' ');
  };

  const trProps = useAccessibleElementProps({
    className: getClassName(),
    // only needed for logic, will be overridden by the single/double click handlers directly
    onClick: rowClick
  });

  // row needs to be prepared before every render
  prepareRow(row);
  return (
    <tr
      {...row.getRowProps()}
      {...trProps}
      key={`table-row-${rowIndex}`}
      onClick={rowSingleClickHandler}
      onDoubleClick={rowDoubleClickHandler}
      // set `aria-selected` only when the row selection plugin is present
      {...(typeof row?.isSelected === 'boolean' && {
        'aria-selected': !!row?.isSelected
      })}
    >
      {row.cells.map((cell: CustomCell<RowDataType>, cellIndex: number) => {
        return (
          <td
            {...cell.getCellProps()}
            className={`${
              isResponsiveView
                ? `flex-order-${cell.column.breakOrder || cellIndex + 1}`
                : ''
            }`}
            key={`cell-${rowIndex}-${cellIndex}`}
            {...(hideableColumns &&
              (rowActions?.length ?? 0) <= 0 &&
              cellIndex === row.cells.length - 1 && {
                colSpan: 2
              })}
          >
            {cell.render('Cell')}
          </td>
        );
      })}
      {rowActions && rowActions.length > 0 && (
        <td
          className="action-col"
          role="cell"
          {...(hideableColumns && {
            colSpan: 2
          })}
        >
          <HBox className="actions-hbox">
            {!isResponsiveView &&
              !squashActions &&
              rowActions.length <= 3 &&
              rowActions.map(
                (action: TableAction<RowDataType>, actionIndex: number) => (
                  <Button
                    key={`action-${actionIndex}`}
                    variant="gray"
                    icon={action.icon}
                    text={action.label || action.icon}
                    className="action-button"
                    disabled={isActionDisabled(action, row)}
                    onClick={() => handleActionClick(action, row)}
                  />
                )
              )}
            {isResponsiveView || squashActions || rowActions.length > 3 ? (
              <MenuTool className="action-button">
                {rowActions.map(
                  (action: TableAction<RowDataType>, actionIndex: number) => (
                    <Tool
                      key={`action-${actionIndex}`}
                      text={action.label || ''}
                      icon={action.icon}
                      onClick={() => handleActionClick(action, row)}
                      disabled={isActionDisabled(action, row)}
                    />
                  )
                )}
              </MenuTool>
            ) : null}
          </HBox>
        </td>
      )}
    </tr>
  );
}
